/** Canonical paywall entrypoint */
const PAYWALL_PATH = "/paywall";

/** Fired when we send the user to `/paywall` so billing status can refresh (e.g. pack ended → 402). */
export const BILLING_ACCESS_REVOKED_EVENT = "tessera:billing-access-revoked";

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

/**
 * Used when a gated API returns 402.
 *
 * Important: do NOT hard-reload the page (assign/hash), otherwise any view that
 * is already making gated requests can create a reload storm. Use history state
 * navigation instead, and no-op when already on the paywall.
 */
export function redirectToBillingPaywall(): void {
  if (typeof window === "undefined") return;

  const current = normalizePathname(window.location.pathname);
  if (current === PAYWALL_PATH) return;

  window.dispatchEvent(new Event(BILLING_ACCESS_REVOKED_EVENT));

  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  try {
    window.history.pushState({ from: returnTo }, "", PAYWALL_PATH);
    window.dispatchEvent(new PopStateEvent("popstate"));
  } catch {
    window.location.assign(PAYWALL_PATH);
  }
}

/**
 * Leave `/paywall` once Tier 1 access is confirmed (e.g. after refresh or checkout).
 * Restores the pre-paywall route from history state when available.
 */
export function redirectFromBillingPaywall(fallbackPath = "/dashboard"): void {
  if (typeof window === "undefined") return;

  const current = normalizePathname(window.location.pathname);
  if (current !== PAYWALL_PATH) return;

  const state = window.history.state as { from?: unknown } | null;
  const from =
    typeof state?.from === "string" && state.from.trim()
      ? state.from
      : fallbackPath;
  const target = normalizePathname(from.split(/[?#]/)[0] ?? fallbackPath);
  const destination =
    target === PAYWALL_PATH || !target.startsWith("/") ? fallbackPath : from;

  try {
    window.history.pushState({}, "", destination);
    window.dispatchEvent(new PopStateEvent("popstate"));
  } catch {
    window.location.assign(destination);
  }
}
